/* global messg */
import { kea } from 'kea'
import { put, call, fork, take, race, select, cancelled } from 'redux-saga/effects'
import NProgress from 'nprogress'
import { router } from 'kea-router'

import I18n from 'lib/i18n'
import pathFromUrl from 'lib/utils/path-from-url'
import { addLanguageUrlToPath, removeLanguageUrlFromPath } from 'lib/utils/links'
import geolocate from 'lib/utils/promise-geolocate'
import delay from 'lib/utils/delay'
import scrollToView from 'lib/utils/scroll-to-view'
import { scrollToTop } from 'lib/utils/scroll-to-top'

import { getKeyFromFilter, updateFilterInSearchPath } from '~/scenes/search/utils'
import { MAX_ZOOM_FOR_SEARCH } from '~/scenes/search/search-page/searchbox'
import Bugsnag from '~/scenes/bugsnag'
import filterLogic from '~/scenes/search/filters-page/logic.js'

import railsContext from 'lib/selectors/rails-context'
import sceneLogic from './logic'
import mapLogic from './search-page/map/logic'

import controller from './controller.rb'

export default kea({
  connect: {
    props: [
      sceneLogic,
      [
        'filter',
        'results',
        'options',
        'searchPathCache',
        'searchPathReverseCache',
        'isLargeScreen',
        'isLandingPageUrl',
        'isLandingPageUrl'
      ]
    ],
    actions: [
      sceneLogic,
      [
        'setLoadingDebounced',
        'updateFilter',
        'setNewFilter',
        'setOpenFilters',
        'receiveResults',
        'triggerLocationChange',
        'setIsLandingPageUrl',
        'searchRequest',
        'receiveFavourites',
        'focusResult',
        'geolocate',
        'silentGeolocate',
        'setGeolocating',
        'clearGeolocating',
        'geolocateDone',
        'translateResultRequest',
        'translateResultSuccess',
        'updateSearchPathCache',
        'updateSearchPathCache',
        'setResultsHeight'
      ],
      mapLogic,
      ['updateMapCenterZoom'],
      filterLogic,
      ['clearChanges', 'updateUnsavedFilter'],
      router,
      ['push', 'locationChanged']
    ]
  },

  takeLatest: ({ actions, workers }) => ({
    [actions.locationChanged]: workers.locationChanged,
    [actions.triggerLocationChange]: workers.triggerLocationChange,
    [actions.updateFilter]: workers.updateFilter,
    [actions.setOpenFilters]: workers.setOpenFilters,
    [actions.searchRequest]: [workers.searchRequest, workers.searchRequestDebouncedLoading]
  }),

  takeEvery: ({ actions, workers }) => ({
    [actions.translateResultRequest]: workers.translateResult,
    [actions.focusResult]: workers.focusResult,
    [actions.geolocate]: workers.geolocate,
    [actions.silentGeolocate]: workers.silentGeolocate
  }),

  * start () {
    if (typeof window === 'undefined') {
      return
    }

    yield fork(this.workers.goToFirstPageIfNoResults)
    yield fork(this.workers.geolocateIfNeeded)
  },

  workers: {
    * goToFirstPageIfNoResults () {
      const { updateFilter } = this.actions

      const results = yield this.get('results')

      if (results.count === 0 && results.page > 1) {
        yield put(updateFilter({ page: 1 }))
      }
    },

    * setOpenFilters (action) {
      const { push } = this.actions
      const { filters } = action.payload

      const { search_path: searchPath } = yield this.get('results')

      const newUrl = updateFilterInSearchPath(searchPath, 'filters', filters)
      const searchPathReverseCache = yield this.get('searchPathReverseCache')

      const finalNewUrl = addLanguageUrlToPath(
        searchPathReverseCache[newUrl] || searchPathReverseCache[newUrl + '/'] || newUrl
      )

      yield put(push(finalNewUrl))
    },

    * updateFilter (action) {
      const { setNewFilter, searchRequest } = this.actions
      const { filterChanges, meta } = action.payload

      const oldFilter = yield this.get('filter')

      let updateFilterAfterSearch = false

      const filter = { ...oldFilter, ...filterChanges }

      // if updating the favourites page on/off, wait until we get the results to update the screen
      // otherwise the map will disappear and the page will load and look funny
      if (typeof filterChanges.favourites !== 'undefined' && oldFilter.favourites !== filterChanges.favourites) {
        updateFilterAfterSearch = true
      }

      // clear location slug if searching for a new location
      if (filterChanges.location && !filterChanges.location_slug) {
        if (oldFilter.location !== filter.location) {
          filter.location_slug = null
        } else if (oldFilter.location_slug) {
          filter.location_slug = oldFilter.location_slug
        }
      }

      // clear bounds if setting new center & zoom
      if (filterChanges.center && filterChanges.zoom && !filterChanges.bound) {
        filter.bounds = null
      }

      if (filterChanges.fromLocationAutocomplete) {
        filter.from_map = false
      }

      if (filterChanges.location) {
        updateFilterAfterSearch = true
      }

      if (!updateFilterAfterSearch) {
        yield put(setNewFilter(filter, meta))
      }

      yield put(
        searchRequest({
          params: filter,
          meta: {
            ...meta,
            updateFilterAfterSearch,
            fromUpdateFilter: true
          }
        })
      )
    },

    // called for the latest search action that's sent, cancelled if not finished before
    * searchRequest (action) {
      const { receiveResults, setNewFilter, setIsLandingPageUrl, updateSearchPathCache, push } = this.actions
      const { params, meta } = action.payload
      const { updateFilterAfterSearch, fromUpdateFilter, fromDimensionUpdate, fromHistory } = meta
      const isLandingPageUrl = yield this.get('isLandingPageUrl')

      const { location_types: LocationTypes, teacher_country_code: teacherCountryCode } = params
      const isWebcamLocation = LocationTypes === 'webcam'

      try {
        if (!fromDimensionUpdate) {
          NProgress.start()
          NProgress.set(0.1)
        }

        const response = yield controller.index(params)

        const oldFilter = yield this.get('filter')
        const oldResults = yield this.get('results')
        const oldPath = oldResults.path

        const filterWithOldFilters = { ...response.filter, filters: oldFilter.filters }

        yield put(receiveResults(response.results, filterWithOldFilters, meta))

        if (fromHistory) {
          yield put(setNewFilter(response.filter))
        } else {
          if (updateFilterAfterSearch) {
            yield put(setNewFilter(filterWithOldFilters))
          }

          const newPath = response.results.path

          if (oldPath !== newPath) {
            if (fromDimensionUpdate) {
              yield put(updateSearchPathCache({ [oldPath]: newPath }))
            } else {
              const searchPathReverseCache = yield this.get('searchPathReverseCache')
              yield put(push(searchPathReverseCache[newPath] || searchPathReverseCache[newPath + '/'] || newPath))
            }
          }

          if (!fromDimensionUpdate) {
            if (!isWebcamLocation || (isWebcamLocation && teacherCountryCode)) {
              yield put(setIsLandingPageUrl(false))
            } else if (!isLandingPageUrl) {
              yield put(setIsLandingPageUrl(true))
            }
          }
        }

        if (fromUpdateFilter && !fromDimensionUpdate && !fromHistory) {
          scrollToTop()
        }
      } catch (e) {
        if (!(yield cancelled())) {
          Bugsnag.notify(e)
        }
      } finally {
        if (!fromDimensionUpdate) {
          NProgress.done()
        }
      }
    },

    // triggered with the url changes
    * locationChanged (action) {
      const { searchRequest, setNewFilter, clearChanges } = this.actions
      const { pathname, search } = action.payload

      const fullNewPath = decodeURI(pathname + search)
      const fullOldPath = (yield this.get('results')).path
      const oldFilter = yield this.get('filter')

      const newPath = (removeLanguageUrlFromPath(fullNewPath) || '').split('?')[0]
      const oldPath = (removeLanguageUrlFromPath(fullOldPath) || '').split('?')[0]

      const searchPathCache = yield this.get('searchPathCache')

      try {
        const newWithoutFilters = updateFilterInSearchPath(searchPathCache[newPath] || newPath, 'filters', '')
        const oldWithoutFilters = updateFilterInSearchPath(searchPathCache[oldPath] || oldPath, 'filters', '')

        // without filters the URLs are same, so just set the new URL without making a request
        if (newWithoutFilters === oldWithoutFilters) {
          const newFilters = newPath.indexOf('/s/') === 0 ? getKeyFromFilter(newPath, 'filters') : ''
          yield put(setNewFilter({ ...oldFilter, filters: newFilters }))
          return
        }
        yield put(clearChanges())
      } catch (e) {
        // ignore the catch block.
        // one of the URLs was something we could not parse, e.g. a /course-maths/bruxelles url that we didn't have cached
      }

      if (newPath === oldPath) {
        return
      }

      const mapDiv = typeof window !== 'undefined' && window.document.getElementById('search-page-main-pigeon-map')
      const width = mapDiv ? mapDiv.clientWidth : oldFilter.width
      const height = mapDiv ? mapDiv.clientHeight : oldFilter.height

      if (newPath.indexOf('/s/') === 0) {
        // search url with /s/*
        const [, , locationPuzzle, query, center, zoom, perPage, page, filter] = newPath.split('/')

        const params = {
          location_puzzle: locationPuzzle,
          query,
          center,
          zoom,
          per_page: perPage,
          page,
          filter,
          width,
          height
        }

        yield put(searchRequest({ params, meta: { fromHistory: true } }))
      } else if (newPath.split('/')[1] === 'favourites') {
        yield put(searchRequest({ params: { filter: 'favourites=true' }, meta: { fromHistory: true } }))
      } else {
        // search url with slugs
        const [, querySlug, locationPuzzle, page] = newPath.split('/')

        const params = {
          location_puzzle: locationPuzzle,
          query_slug: querySlug,
          page,
          width,
          height
        }

        yield put(searchRequest({ params, meta: { fromHistory: true } }))
      }
    },

    * triggerLocationChange (action) {
      const { push } = this.actions
      const { location } = action.payload
      yield put(push(pathFromUrl(location)))
    },

    * geolocateIfNeeded () {
      const { setNewFilter } = this.actions
      const options = yield this.get('options')

      if (options.try_geolocation) {
        const loadCounter = yield select(state => railsContext(state).loadCounter)

        const fastest = yield race({
          delay: delay(loadCounter === 1 ? 10000 : 3000),
          filter: take(
            a =>
              a.type === setNewFilter.toString() && a.payload && a.payload.filter && !a.payload.meta.fromDimensionUpdate
          )
        })

        if (fastest.delay) {
          yield call(this.workers.geolocate, {}, true)
        }
      }
    },

    * silentGeolocate (action = null, failSilently = false) {
      const { updateUnsavedFilter } = this.actions

      try {
        if ('geolocation' in navigator) {
          const center = yield geolocate()

          const response = yield controller.coordsToCity({ lat: center[0], lng: center[1], zoom: MAX_ZOOM_FOR_SEARCH })

          if (response) {
            yield put(
              updateUnsavedFilter({
                center: center,
                zoom: MAX_ZOOM_FOR_SEARCH,
                page: 1,
                zoom_out_if_needed: true,
                location: response.location || I18n.t('react.search.geolocation.unknown_location')
              })
            )
          }
        }
      } catch (e) {
        if (!failSilently) {
          messg.error(I18n.t('react.search.geolocation.no_permission_error'), 4000)
        }
      }
    },

    * geolocate (action = null, failSilently = false) {
      // if action === null then fromGeolocation = true

      const { updateFilter, setGeolocating, clearGeolocating, geolocateDone, updateMapCenterZoom } = this.actions

      try {
        if ('geolocation' in navigator) {
          yield put(setGeolocating())
          if (!failSilently) {
            NProgress.start()
          }
          const center = yield geolocate()

          if (!failSilently) {
            NProgress.done()
          }

          yield put(updateMapCenterZoom(center, MAX_ZOOM_FOR_SEARCH))

          if (this.values.isLargeScreen) {
            yield put(
              updateFilter({
                center: center,
                zoom: MAX_ZOOM_FOR_SEARCH,
                page: 1,
                zoom_out_if_needed: true
              })
            )
          }

          yield put(geolocateDone())
          yield put(clearGeolocating())
        }
      } catch (e) {
        yield put(clearGeolocating())
        if (!failSilently) {
          NProgress.done()
          messg.error(I18n.t('react.search.geolocation.no_permission_error'), 4000)
        }
      }
    },

    * translateResult (action) {
      const { translateResultSuccess } = this.actions
      const { resultId, locale } = action.payload
      const response = yield controller.translate({ id: resultId, locale })
      if (response.result) {
        yield put(translateResultSuccess(response.result))
      }
    },

    focusResult (action) {
      const { id } = action.payload

      if (typeof window === 'undefined') {
        return
      }

      const element = window.document.getElementById(`result-${id}`)

      if (!element) {
        return
      }

      scrollToView({ element, adjustment: -121 })
    },

    * searchRequestDebouncedLoading (action) {
      const { fromDimensionUpdate, fromHistory } = action.payload.meta
      const { setLoadingDebounced, receiveResults } = this.actions

      if (fromDimensionUpdate) {
        return
      }

      if (fromHistory) {
        yield put(setLoadingDebounced())
        return
      }

      const { defer } = yield race({
        defer: delay(900),
        success: take(receiveResults)
      })

      if (defer) {
        yield put(setLoadingDebounced())
      }
    }
  }
})
